import React, {cloneElement, useCallback, useMemo} from "react";
import {Box, Card, CircularProgress, Paper, Skeleton, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {ReactSortable} from "react-sortablejs";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import {Delete} from "@mui/icons-material";
import IconButton from "../../../../../../../components/base/button/icon_button";
import UIUtils from "../../../../../../../utils";

const IndustryManagementViewWitAccessColumn = ({moveIndustry, industryCloned, ...props}) => {
    const dispatch = useDispatch();

    const data = useMemo(() => props.data.map(e => ({...e, filtered: true})), [props.data]);

    /**
     * Remove industry from access list
     * @type {(function(*): void)}
     */
    const removeIndustry = useCallback((industry) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `the Industry: "${industry.title}" from your organization?  remember you can add it back later. shall we proceed`,
            onClose: async (confirmed, data) => {
                if (!confirmed)
                    return;
                return await moveIndustry(industry, props.data.findIndex(e => e.id === industry.id));
            }
        }));
    }, [dispatch, props.data, moveIndustry])

    return (
        <>
            <Paper
                sx={({
                    width: {xs: '100%', md: 'calc(60% - 4px)', lg: 'calc(70% - 4px)'},
                    padding: 2,
                    gap: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    zIndex: 1,
                    height: 'fit-content',
                })}
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: "space-between"}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        {props.title}
                    </Typography>
                </Box>
                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    paddingBlock: 1,
                    paddingInlineEnd: 0.5,
                    position: 'relative',
                }}>
                    <ReactSortable
                        id={`industry-${props.id}`}
                        setList={() => void 0}
                        list={data}
                        handle={'.handle'}
                        ignore={'.ignore'}
                        group={{
                            name: "industries",
                            checkPull: () => true,
                            pull: () => false,
                        }}
                        animation={210}
                        dragoverBubble
                        disabled={props.loading || props.movingIndustry}
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            flexGrow: 1,
                            ...(!props.loading && {
                                minHeight: 100,
                            }),
                        }}
                    >
                        {
                            data.map(industry => (
                                <Box
                                    key={industry.id}
                                    className={'py-2 px-2'}
                                    sx={{
                                        width: {
                                            xs: 'calc(100%)',
                                            sm: 'calc(50%)',
                                            lg: 'calc(33.33%)',
                                        }
                                    }}
                                >
                                    <Card

                                        variant={'outlined'}
                                        sx={{
                                            padding: 1,
                                            gap: 0.5,
                                            color: 'text.primary',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: 'fit-content',
                                            width: '100%',
                                        }}
                                    >
                                        {
                                            cloneElement(
                                                UIUtils.getIndustryIcon(industry?.iconFileName, industry?.id) ?? <div/>,
                                                {
                                                    style: {width: 24, height: 24, borderRadius: 8},
                                                }
                                            )
                                        }
                                        <Typography variant={'body2'} fontWeight={600} sx={{marginInlineEnd: 'auto'}}>
                                            {industry.title}
                                        </Typography>
                                        <IconButton
                                            className={"mx-1"}
                                            color={'paper.error'}
                                            size={'small'}
                                            component={'span'}
                                            disabled={props.loading || props.movingIndustry}
                                            onPressed={() => removeIndustry(industry)}
                                        >
                                            <Delete className={'icon small'}/>
                                        </IconButton>
                                    </Card>
                                </Box>
                            ))
                        }
                        <Box
                            key={'ignore'}
                            className={'ignore'}
                            sx={(theme) => ({
                                position: 'absolute',
                                top: 0,
                                borderRadius: 1,
                                left: 0,
                                width: 'calc(100% + 32px)',
                                height: 'calc(100% + 32px)',
                                margin: -2,
                                backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.3)`,
                                zIndex: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                ...(!industryCloned && !props.movingIndustry && {
                                    display: 'none',
                                }),
                            })}
                        >
                            <Box sx={{
                                backdropFilter: 'blur(9px)',
                                backgroundColor: 'background.tertiary',
                                borderRadius: 1,
                                padding: 1,
                            }}>
                                <Typography
                                    variant={'subtitle2'}
                                    color={'primary.main'}
                                    component={'div'}
                                    sx={{display: 'flex', alignItems: 'center'}}
                                    fontWeight={600}
                                >
                                    {
                                        industryCloned &&
                                        "Drag the industry here to add it to your organization"
                                    }
                                    {
                                        props.movingIndustry &&
                                        <>
                                            Adding the selected industry into your organization...
                                            <CircularProgress size={20} sx={{marginInline: 1}}/>
                                        </>
                                    }
                                </Typography>
                            </Box>
                        </Box>
                    </ReactSortable>
                    {
                        props.loading &&
                        <Grid container spacing={2}>
                            {
                                Array.from({length: 10}).map((_, key) => (
                                    <Grid key={`loading-${key}`} xs={12} sm={6} lg={4}>
                                        <Skeleton
                                            width={'100%'}
                                            sx={{minHeight: 48, transformOrigin: 'none', transform: 'none'}}
                                        />
                                    </Grid>
                                ))
                            }
                        </Grid>
                    }
                </Box>
            </Paper>
        </>
    );
}


export default IndustryManagementViewWitAccessColumn;
